const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const STYLE_TAG_ID = 'cart-qty-btn-style';

// cart-drawer-stylesheet.css is loaded non-blocking (media="print" swap in
// sections/cart-drawer.liquid), so it can still be in flight when a shopper taps
// the cart. Opening before it applies paints the panel with only the structural
// fallback in base.css. These helpers let open() wait for the real sheet, and let
// intent signals start fetching it early.
const CART_CSS_LINK = 'link[href*="cart-drawer-stylesheet"]:not([rel~="preload"])';
const CART_CSS_WAIT_MS = 1500;
const CART_INTENT_SELECTOR = '[data-cart-trigger], [data-atc-btn], [data-add-to-cart]';
const INTENT_LISTENER_OPTIONS = { passive: true, capture: true };

const cartStylesheetLink = () => document.querySelector(CART_CSS_LINK);

// Applied means matching-media AND parsed. A print-media link can be fully
// downloaded and still contribute nothing to layout.
const cartStylesheetReady = () => {
  const link = cartStylesheetLink();
  if (!link) return true; // cart page loads it render-blocking; nothing to wait for
  return link.media !== 'print' && !!link.sheet;
};

const promoteCartStylesheet = () => {
  const link = cartStylesheetLink();
  if (link?.media === 'print') {
    link.onload = null;
    link.media = 'all';
  }
};

const whenCartStylesheetReady = (timeout) =>
  new Promise((resolve) => {
    if (cartStylesheetReady()) {
      resolve(true);
      return;
    }

    const link = cartStylesheetLink();
    let settled = false;

    const done = (applied) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      link?.removeEventListener('load', onLoad);
      link?.removeEventListener('error', onLoad);
      resolve(applied);
    };

    // Safari can leave a swapped sheet parsed but not yet adopted; the rAF
    // forces the style recalculation that adopts it before we reveal.
    const onLoad = () => requestAnimationFrame(() => done(true));
    const timer = setTimeout(() => done(false), timeout);

    link?.addEventListener('load', onLoad, { once: true });
    link?.addEventListener('error', onLoad, { once: true });
  });

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.previousActiveElement = null;
    this.isOpen = false;
    this.focusTrapHandler = null;
    this.quantityDebounceTimers = {};
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-cart-panel]');

    this._bindOverlayClose();

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
      // NOTE: checkout is triggered by the button's own inline
      // onclick="handleFloCheckoutBtn()" (see cart-footer.liquid / cart.liquid),
      // not this delegated listener — the button markup never carries
      // [data-checkout-btn], so _handleCheckout() below is unreachable.
      // Kept as documentation only; do not wire it up without first removing
      // the inline onclick, or handleFloCheckoutBtn() will fire twice per click.
      if (event.target.closest('[data-quantity-minus]')) this._onQtyButtonClick(event.target.closest('[data-quantity-minus]'), -1);
      if (event.target.closest('[data-quantity-plus]')) this._onQtyButtonClick(event.target.closest('[data-quantity-plus]'), 1);
      if (event.target.closest('[data-remove-item]')) {
        const btn = event.target.closest('[data-remove-item]');
        this.updateQuantity(parseInt(btn.dataset.index, 10), 0);
      }
      if (event.target.closest('[data-cart-clear-all]')) this.clearCart();
    });

    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.handleKeydown);

    this.handleCartTrigger = (event) => {
      if (event.target.closest('[data-cart-trigger]')) {
        event.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this.handleCartTrigger);

    // Start fetching the drawer stylesheet on intent: pointerdown/touchstart fire
    // ~100ms before click, and again once the page is idle after load. Both are
    // after LCP, so the critical path is untouched.
    this.handleCartIntent = (event) => {
      if (event.target.closest?.(CART_INTENT_SELECTOR)) promoteCartStylesheet();
    };
    document.addEventListener('pointerdown', this.handleCartIntent, INTENT_LISTENER_OPTIONS);
    document.addEventListener('touchstart', this.handleCartIntent, INTENT_LISTENER_OPTIONS);

    this.handleWindowLoad = () => {
      const idle = window.requestIdleCallback
        ? window.requestIdleCallback.bind(window)
        : (callback) => setTimeout(callback, 200);
      idle(() => promoteCartStylesheet());
    };
    if (document.readyState === 'complete') this.handleWindowLoad();
    else window.addEventListener('load', this.handleWindowLoad, { once: true });

    this.unsubscribeCartAdd = subscribe(PUB_SUB_EVENTS.cartAdd, (data) => {
      this.renderContents(data);
    });

    this._initSliders();
  }

  disconnectedCallback() {
    this.unsubscribeCartAdd?.();
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleCartTrigger);
    document.removeEventListener('pointerdown', this.handleCartIntent, INTENT_LISTENER_OPTIONS);
    document.removeEventListener('touchstart', this.handleCartIntent, INTENT_LISTENER_OPTIONS);
    window.removeEventListener('load', this.handleWindowLoad);
    this._removeTrapFocus();
  }

  openCart() {
    setTimeout(() => this.open(), 200);
  }

  _lockScroll() {
    if (this._scrollY !== undefined) return;
    this._scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  _unlockScroll() {
    if (this._scrollY === undefined) return;
    const scrollY = this._scrollY;
    this._scrollY = undefined;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  open() {
    clearTimeout(this._closeTimer);
    this.previousActiveElement = document.activeElement;
    this.isOpen = true;

    if (cartStylesheetReady()) {
      this._reveal();
      return;
    }

    // Stay display:none rather than paint a panel the stylesheet hasn't styled
    // yet. Capped so a slow or dead CDN degrades to the old behaviour instead of
    // an unopenable cart.
    promoteCartStylesheet();
    whenCartStylesheetReady(CART_CSS_WAIT_MS).then(() => {
      if (this.isOpen) this._reveal();
    });
  }

  _reveal() {
    this.hidden = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.classList.add('active');
        this._lockScroll();
        this.setAttribute('aria-hidden', 'false');
        this.panel?.addEventListener('transitionend', () => this._trapFocus(), { once: true });
      });
    });
  }

  close() {
    this.isOpen = false;
    this._removeTrapFocus();
    this.classList.remove('active');
    this._unlockScroll();
    this.setAttribute('aria-hidden', 'true');

    this._closeTimer = setTimeout(() => {
      this.hidden = true;
      this.previousActiveElement?.focus();
    }, 300);
  }

  closeBothDrawer() {
    this.close();
  }

  renderContents(data) {
    const sectionId = this.dataset.sectionId;
    const sectionHtml = data.sections?.[sectionId] ?? data.sections?.['cart-drawer'];
    if (!sectionHtml) return;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newDrawer = parsed.querySelector('cart-drawer');
    if (!newDrawer) return;

    this.innerHTML = newDrawer.innerHTML;

    this.panel = this.querySelector('[data-cart-panel]');

    this._bindOverlayClose();

    if (this.isOpen) {
      this.classList.add('active');
    }

    this._syncCountFromHeader();
    this._initSliders();

    document.dispatchEvent(new CustomEvent('cart:rendered'));
  }

  async refreshCartIconBubble() {
    try {
      const res = await fetch('/?sections=header');
      const data = await res.json();
      const html = data?.sections?.header ?? data?.header;
      if (!html) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        const newBadge = parsed.querySelector('[data-cart-count]');
        if (newBadge) badge.textContent = newBadge.textContent;
      });
    } catch (_) {
    }
  }

  _handleCheckout() {
    if (typeof handleFloCheckoutBtn === 'function') {
      handleFloCheckoutBtn();
    }
    // No native-checkout fallback. Shopflo owns checkout, and it already
    // fails open to /checkout itself when its token API errors. Routing here
    // as well would send shoppers to native Shopify checkout whenever the
    // bundle is merely slow to initialise — losing the prepaid discount, COD
    // flow and upsells. The reference theme has no such fallback either.
    // else {
    //   window.location.href = '/cart/checkout';
    // }
  }

  _bindOverlayClose() {
    const overlay = this.querySelector('[data-cart-overlay]');
    overlay?.addEventListener('click', () => this.closeBothDrawer());
  }

  _showScreenOverlay(show) {
    const overlay = document.querySelector('[data-atc-screen-overlay]');
    if (!overlay) return;
    overlay.style.removeProperty('display');
    overlay.classList.toggle('is-visible', show);
  }

  _syncCountFromHeader() {
    const countSpan = this.querySelector('[data-cart-item-count]');
    if (!countSpan) {
      updateCartCount(0);
      return;
    }
    const raw = countSpan.textContent.replace(/[()]/g, '').trim();
    const count = parseInt(raw, 10) || 0;
    updateCartCount(count);
  }

  _trapFocus() {
    if (!this.panel) return;
    const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTORS)];
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first.focus();

    this._removeTrapFocus();

    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === first) { event.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };

    this.panel.addEventListener('keydown', this.focusTrapHandler);
  }

  _removeTrapFocus() {
    if (this.focusTrapHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  _onQtyButtonClick(button, delta) {
    const index = parseInt(button.dataset.index, 10);
    const input = this.querySelector(`[data-quantity-input][data-index="${index}"]`);
    if (!input) return;

    const currentQty = parseInt(input.value, 10);
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === currentQty) return;

    // Show loader immediately on click for all cart modes
    const itemEl = this.querySelector(`#CartItem-${index}`);
    const loadingEl = itemEl?.querySelector('.cart-line-item__loading');
    if (loadingEl) loadingEl.classList.add('flex');
    this._showScreenOverlay(true);

    clearTimeout(this.quantityDebounceTimers[index]);
    this.quantityDebounceTimers[index] = setTimeout(() => {
      if (!this._validateQuantity(input, newQty)) {
        if (loadingEl) loadingEl.classList.remove('flex');
        this._showScreenOverlay(false);
        return;
      }
      this.updateQuantity(index, newQty);
    }, 150);
  }

  _validateQuantity(input, newQty) {
    const strings = window.quickOrderListStrings ?? {};
    const min = 0;
    const max = parseInt(input.max, 10) || Infinity;
    const step = parseInt(input.step, 10) || 1;

    const errorEl = this.querySelector(`#CartItem-${input.dataset.index} [data-qty-error]`);

    const showError = (msg) => {
      if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
    };

    if (newQty > 0 && newQty < 1) {
      showError(strings.min_error ?? `Minimum quantity is ${min}`);
      return false;
    }
    if (newQty > max) {
      showError(strings.max_error ?? `Maximum quantity is ${max}`);
      return false;
    }
    if (newQty > 0 && newQty % step !== 0) {
      showError(strings.step_error ?? `Quantity must be a multiple of ${step}`);
      return false;
    }

    if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
    return true;
  }

  async updateQuantity(lineIndex, quantity) {
    const sectionId = this.dataset.sectionId;

    // Show per-item loading spinner
    const itemEl = this.querySelector(`#CartItem-${lineIndex}`);
    const loadingEl = itemEl?.querySelector('.cart-line-item__loading');
    if (loadingEl) loadingEl.classList.add('flex');

    this._showScreenOverlay(true);

    const statusEl = this.querySelector('[data-cart-status]');
    if (statusEl) statusEl.textContent = 'Cart is updating';

    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    const itemsContainer = this.querySelector('[data-cart-items]');
    if (itemsContainer) itemsContainer.style.pointerEvents = 'none';

    try {
      const data = await CartAPI.change(lineIndex, quantity, [sectionId]);

      this.renderContents(data);
      this._injectMinusBtnStyle(data);

      if (statusEl) statusEl.textContent = 'Cart has been updated';

      const updatedInput = this.querySelector(`[data-quantity-input][data-index="${lineIndex}"]`);
      updatedInput?.focus();

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: data });
    } catch (error) {
      const input = this.querySelector(`[data-quantity-input][data-index="${lineIndex}"]`);
      if (input) input.value = input.defaultValue;
      publish(PUB_SUB_EVENTS.cartError, { source: 'cart-drawer', error });
    } finally {
      this._showScreenOverlay(false);
      const container = this.querySelector('[data-cart-items]');
      if (container) container.style.pointerEvents = '';
    }
  }

  // Empties the WHOLE cart in one call via /cart/clear.js, rather than firing
  // one /cart/change.js per line.
  async clearCart() {
    // confirm() guarded against a stray tap wiping every line at once (unlike
    // a single-line remove, this can't be undone by re-adding just one item)
    // — disabled per request.
    // if (!window.confirm('Remove all items from your cart?')) return;

    const sectionId = this.dataset.sectionId;

    this._showScreenOverlay(true);

    const statusEl = this.querySelector('[data-cart-status]');
    if (statusEl) statusEl.textContent = 'Cart is updating';

    const itemsContainer = this.querySelector('[data-cart-items]');
    if (itemsContainer) itemsContainer.style.pointerEvents = 'none';

    try {
      const data = await CartAPI.clear([sectionId]);

      this.renderContents(data);

      if (statusEl) statusEl.textContent = 'Cart has been updated';

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: data });
    } catch (error) {
      publish(PUB_SUB_EVENTS.cartError, { source: 'cart-drawer', error });
    } finally {
      this._showScreenOverlay(false);
      const container = this.querySelector('[data-cart-items]');
      if (container) container.style.pointerEvents = '';
    }
  }

  _injectMinusBtnStyle(data) {
    document.getElementById(STYLE_TAG_ID)?.remove();

    const items = data?.items ?? [];
    const rules = items
      .map((item) => {
        const qty = item.quantity;
        const variantId = item.variant_id;
        const bg = qty === 1 ? '#fdfdfd' : '#FFF';
        return `[data-quantity-minus][data-quantity-variant-id="${variantId}"] { background-color: ${bg}; }`;
      })
      .join('\n');

    if (!rules) return;

    const style = document.createElement('style');
    style.id = STYLE_TAG_ID;
    style.textContent = rules;
    document.head.appendChild(style);
  }

  _initSliders() {
    this.querySelectorAll('[data-cart-slider-track], [data-cart-recs-slider-track]').forEach((track) => {
      this._attachSlider(track);
    });
  }

  _attachSlider(track) {
    if (!track || track.dataset.sliderInit) return;
    track.dataset.sliderInit = '1';

    const wrapper = track.parentElement;
    if (!wrapper) return;

    // Touch devices use native momentum scrolling + CSS scroll-snap. Driving
    // scrollLeft from JS on top of that makes the two fight frame by frame,
    // which reads as the track jumping forward then snapping back mid-swipe.
    if (window.matchMedia('(pointer: coarse)').matches) return;

    let startX = 0;
    let scrollStart = 0;

    const onMouseMove = (event) => {
      wrapper.scrollLeft = scrollStart + (startX - event.clientX);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      wrapper.classList.remove('is-dragging');

      const item = track.querySelector('.cart-drawer__slide-item, .cart-drawer__rec-item');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      const snapUnit = (item?.offsetWidth ?? 144) + gap;
      const snapped = Math.round(wrapper.scrollLeft / snapUnit) * snapUnit;
      wrapper.scrollTo({ left: snapped, behavior: 'smooth' });
    };

    const onMouseDown = (event) => {
      startX = event.clientX;
      scrollStart = wrapper.scrollLeft;
      wrapper.classList.add('is-dragging');
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    wrapper.addEventListener('mousedown', onMouseDown);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
